/**
 * Zustand store for workspace custom agents.
 */

import { create } from 'zustand';
import type { CustomAgent, UpsertCustomAgentInput } from '../../lib/customAgentService';
import {
  listCustomAgents,
  getCustomAgent,
  upsertCustomAgent,
  deleteCustomAgent,
} from '../../lib/customAgentService';

interface CustomAgentStoreState {
  agents: CustomAgent[];
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchAgents: (workspaceId: string) => Promise<void>;
  fetchAgent: (id: string) => Promise<void>;
  saveAgent: (input: UpsertCustomAgentInput) => Promise<{ success: boolean; agent?: CustomAgent; error?: string }>;
  deleteAgent: (id: string, workspaceId: string) => Promise<{ success: boolean; error?: string }>;
}

export const useCustomAgentStore = create<CustomAgentStoreState>((set, get) => ({
  agents: [],
  isLoading: false,
  isSaving: false,
  isDeleting: false,
  error: null,
  loadedWorkspaceId: null,

  fetchAgents: async (workspaceId: string) => {
    set({ isLoading: true, error: null });
    const { data, error } = await listCustomAgents(workspaceId);
    if (error || !data) {
      set({ isLoading: false, error: error ?? 'Failed to load agents' });
      return;
    }
    set({ agents: data, isLoading: false, loadedWorkspaceId: workspaceId });
  },

  fetchAgent: async (id: string) => {
    const { data, error } = await getCustomAgent(id);
    if (error || !data) return;
    set((state) => ({
      agents: state.agents.some((a) => a.id === id)
        ? state.agents.map((a) => (a.id === id ? data : a))
        : [...state.agents, data],
    }));
  },

  saveAgent: async (input: UpsertCustomAgentInput) => {
    set({ isSaving: true, error: null });
    const { data, error } = await upsertCustomAgent(input);
    if (error || !data) {
      set({ isSaving: false, error: error ?? 'Save failed' });
      return { success: false, error: error ?? 'Save failed' };
    }
    set((state) => ({
      isSaving: false,
      agents: state.agents.some((a) => a.id === data.id)
        ? state.agents.map((a) => (a.id === data.id ? data : a))
        : [...state.agents, data],
    }));
    return { success: true, agent: data };
  },

  deleteAgent: async (id: string, workspaceId: string) => {
    set({ isDeleting: true, error: null });
    const { error } = await deleteCustomAgent(id, workspaceId);
    if (error) {
      set({ isDeleting: false, error });
      return { success: false, error };
    }
    set((state) => ({
      isDeleting: false,
      agents: state.agents.filter((a) => a.id !== id),
    }));
    // If loadedWorkspaceId is set, re-fetch for freshness
    const loaded = get().loadedWorkspaceId;
    if (loaded === workspaceId) {
      await get().fetchAgents(workspaceId);
    }
    return { success: true };
  },
}));
